// What kind of challenge this is: its name, how many phases stand between the
// trader and the funded account, and what happens when one fails — to the
// attempt, and later to its account.

import { For, Show } from 'solid-js';

import type { FailedAccountEndKind } from '../wire';
import { MODE_LABELS, type ChallengeMode } from './builderForm';
import Dropdown from './Dropdown';
import NumberInput from './NumberInput';
import { useOrgWords } from './builderWordsContext';

interface StructureCardProps {
  name: string;
  mode: ChallengeMode;
  resetAllowed: boolean;
  failedAccountEnd: FailedAccountEndKind;
  failedAccountDays: string;
  onName: (name: string) => void;
  onMode: (mode: ChallengeMode) => void;
  onResetAllowed: (allowed: boolean) => void;
  onFailedAccountEnd: (end: FailedAccountEndKind) => void;
  onFailedAccountDays: (days: string) => void;
}

/** The longest a failed account may wait, in days (the contract's limit). */
const MOST_KEPT_DAYS = 365;

const isFailedAccountEnd = (value: string): value is FailedAccountEndKind =>
  value === 'keep_days' || value === 'close_now' || value === 'firm_closes';

const MODES = Object.keys(MODE_LABELS) as ChallengeMode[];

export default function StructureCard(props: StructureCardProps) {
  const words = useOrgWords();
  return (
    <section class="ch-card">
      <label class="ch-field">
        <span class="ch-label">Name</span>
        <input
          class="ch-input ch-name"
          value={props.name}
          required
          onInput={(event) => props.onName(event.currentTarget.value)}
        />
      </label>

      <div class="ch-picks" role="group" aria-label="Structure">
        <For each={MODES}>
          {(mode) => (
            <button
              type="button"
              class="ch-pick"
              aria-pressed={props.mode === mode}
              onClick={() => props.onMode(mode)}
            >
              <strong>{MODE_LABELS[mode].title}</strong>
              <span class="ch-meta">{MODE_LABELS[mode].desc}</span>
            </button>
          )}
        </For>
      </div>

      <div class="ch-grid-2">
        <div class="ch-field">
          <span class="ch-label">After a failed attempt</span>
          <Dropdown
            value={props.resetAllowed ? 'reset' : 'over'}
            options={[
              { value: 'reset', label: 'The trader can reset and try again' },
              { value: 'over', label: `The ${words.run} is over` },
            ]}
            onChange={(value) => props.onResetAllowed(value === 'reset')}
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">The failed account</span>
          <Dropdown
            value={props.failedAccountEnd}
            options={[
              { value: 'keep_days', label: 'Stays open for some days, then closes' },
              // An account closed at the failure leaves no time for a reset.
              ...(props.resetAllowed ? [] : [{ value: 'close_now', label: 'Closes when it fails' }]),
              { value: 'firm_closes', label: 'Stays open until closed by hand' },
            ]}
            onChange={(value) => {
              if (isFailedAccountEnd(value)) props.onFailedAccountEnd(value);
            }}
          />
        </div>
        <Show when={props.failedAccountEnd === 'keep_days'}>
          <div class="ch-field">
            <span class="ch-label">Days it stays open</span>
            <NumberInput
              value={props.failedAccountDays}
              onInput={(value) => props.onFailedAccountDays(value)}
              min={1}
              max={MOST_KEPT_DAYS}
              step={1}
              required
            />
          </div>
        </Show>
      </div>
    </section>
  );
}
